import React, { useCallback, useEffect, useState } from 'react';
import Search from '../../shared/components/ui/Search';
import Table from '../../shared/components/layout/Table';
import filter from '../../../modules/shared/assets/svg/filter.svg';
import { transformData } from '../../super-admin/utils/transformData';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getPerformaInvoice, updatePerformaInvoice } from '../services/api/implementation/impl';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  buildReturnToFromLocation,
  navigateWithReturn
} from '../../shared/navigation';
import { useForm } from 'react-hook-form';
import Modal from '../../shared/components/ui/Modal';
import Button from '../../shared/components/ui/Button';
import DateFormat from '../../super-admin/utils/DateFormat';
import toast from 'react-hot-toast';
import FormInput from '../../shared/components/ui/FormInput';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import NoDataFound from '../components/layout/NoDataFound';
import PaginationComponent from '../../shared/components/PaginationComponent';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import Header from '../../shared/components/layout/Header';
import Layout from '../components/layout/AppLayout';
// import DeleteConfirmationModal from '../components/ui/DeleteConfirmationModal';
import Loader from '../../shared/components/ui/Loader';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { formatISO } from 'date-fns';
import { useProjectsList } from '../../../contexts/ProjectsContext';

const PerformaInvoiceList = () => {
  const userId = Cookies.get('userId');
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
    setValue
  } = useForm();
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedAmount, setSelectedAmount] = useState(0);
  const [dueAmount, setDueAmount] = useState(0);
  const [selctedStage, setSelctedStage] = useState('');
  const { collapsed } = useSidebar();
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  // Removed all blocks/levels related state
  // const [newBlockId, setNewBlockId] = useState(null);
  // const [blocksDynaWithLevels, setBlocksDynaWithLevels] = useState([]);

  // pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10); // items per page

  const {
    data: Payables,
    refetch: refetchPayables,
    isFetching: isFetchingPayables
  } = useQuery({
    queryKey: ['payables', page],
    queryFn: () => getPerformaInvoice(page, limit),
    keepPreviousData: true
  });

  useEffect(() => {
    refetchPayables();
  }, []);

  useEffect(() => {
    if (projects) {
      setValue('project', projects?.[0]?.id);
    }
  }, [projects]);

  const totalPages = Payables?.meta ? Math.ceil(Payables?.meta.total / limit) : 1;

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Removed blocks/levels fetching and useEffect

  const InvoiceFormat = ['id', 'name', 'due_amount', 'paid_amount', 'amount', 'payment_status'];

  const PayableData = Payables?.data?.map((item) =>
    transformData(
      {
        ...item,
        id: item.id,
        name: item?.project?.name,
        due_amount: item?.dueAmount,
        paid_amount: item?.paidAmount,
        amount: item?.amount,
        payment_status: item.isPaid ? 'Paid' : 'ShowPaidButton'
      },
      InvoiceFormat
    )
  );

  // Add this state for multiple payments
  const [paymentAmount, setPaymentAmount] = useState('');
  const [isPaid, setIsPaid] = useState(false);
  const [StatusId, setStatusId] = useState();

  // Dummy register for FormInput when not using react-hook-form
  const dummyRegister = () => ({
    onChange: () => {},
    onBlur: () => {},
    ref: () => {},
    name: ''
  });

  // Refactor handleUpdateStatus to use paySPBills and only paymentAmount
  const { mutate: handleUpdateStatus } = useMutation({
    mutationFn: () => {
      if (!paymentAmount) {
        toast.error('Please enter the payment amount.');
        return Promise.reject();
      }
      const num = Number(paymentAmount);
      // if (!Number.isFinite(num) || num <= 0) {
      //   toast.error('Please enter a valid amount greater than 0.');
      //   return Promise.reject();
      // }
      if (num > Number(dueAmount || 0)) {
        toast.error('Amount should be less than or equal to the pending invoice amount.');
        const err = new Error('VALIDATION_DUE_AMOUNT');
        err.isValidation = true;
        return Promise.reject(err);
      }
      const payload = {
        paidAmount: num
      };
      return updatePerformaInvoice(StatusId, payload);
    },
    onSuccess: () => {
      toast.success('Payment successful');
      setIsPaid(false);
      setPaymentAmount('');
      refetchPayables();
    },
    onError: (err) => {
      if (err?.isValidation || err?.message === 'VALIDATION_DUE_AMOUNT') return;
      toast.error(err?.response?.data?.message || err?.message || 'Error to update payment');
    }
  });

  const handleStatus = (id) => {
    setStatusId(id);
    // Find the full original record to access nested quotation/rfq terms
    const original = Payables?.data?.find((it) => Number(it.id) === Number(id));
    setSelectedInvoice(original || null);

    // Also set the simple fields for convenience
    setSelectedAmount(original?.amount ?? 0);
    setDueAmount(original?.dueAmount ?? 0);
    setSelctedStage(original?.stage ?? '');
    setIsPaid(true);
  };

  // Removed blocks/levels useEffect

  // Reset payments on modal close
  const resetPaidModal = () => {
    setIsPaid(false);
    setSelctedStage('');
    setSelectedAmount(0);
    setPaymentAmount('');
    setSelectedInvoice(null);
    reset();
  };

  const paymentTerms = selectedInvoice?.quotation?.rfq?.paymentTerms || [];

  return (
    <>
      <Loader isVisible={isFetchingPayables} />
      {/* Header */}

      {isPaid && (
        <Modal isDirty={true} confirmOnClose={true} width={55} onClose={resetPaidModal}>
          <div>
            <div className="flex flex-col md:flex-row gap-6 mb-4">
              {/* Left: Payment Terms */}
              <div className="md:w-1/2 w-full bg-gray-50 rounded-md p-4 border border-gray-200 h-fit">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Payment Terms</h3>
                {paymentTerms.length > 0 ? (
                  <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                    {paymentTerms.map((t) => (
                      <li key={t.id}>
                        {t.name} - {t.percentage}%{t.duration ? ` in ${t.duration} days` : ''}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">No payment terms available.</p>
                )}
              </div>

              {/* Right: Existing content */}
              <div className="md:w-1/2 w-full">
                <div className="flex flex-col mb-2">
                  <p className="text-sm text-gray-700 italic font-semibold">
                    You are about to clear the advance to proceed to Purchase order.
                  </p>
                </div>
                <div className="flex justify-center flex-col mb-2">
                  <p className="text-sm">Pending invoice amount - {dueAmount}</p>
                </div>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleUpdateStatus();
                  }}
                  className="flex flex-col gap-4 pt-4">
                  <FormInput
                    width={60}
                    label={'How much are you paying'}
                    isRequired
                    register_key={`payingAmount`}
                    type={'number'}
                    valuePre={paymentAmount}
                    onType={(val) => setPaymentAmount(val.target ? val.target.value : val)}
                    register={dummyRegister}
                  />
                  <div className="w-full flex justify-end items-center space-x-4 mt-4">
                    <Button type="button" onClick={resetPaidModal} displayType="secondary">
                      Cancel
                    </Button>
                    <Button type="submit">Submit</Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </Modal>
      )}

      <div className="w-full flex flex-col  z-5 bg-gray-100">
        {PayableData && PayableData?.length > 0 ? (
          <>
            {/* Grid */}

            <GridWrapper width={100} childrenCustomStyle="m-3">
              <div className="table-container mt-[8.5rem] h-[calc(100vh-11rem)] overflow-auto ">
                <style>{`
            .table-container .table-body {
              position: static !important;
              overflow-y: visible !important;
              max-height: none !important;
              height: auto !important;
              top: auto !important;
              padding-bottom: 0 !important;
            }
            .table-container .table-container {
              height: auto !important;
            }
            .table-container > div {
              height: auto !important;
            }
          `}</style>
                <Table
                  headers={[
                    'Project Name',
                    'Due Amount',
                    'Paid Amount',
                    'Amount',
                    'Payment Status'
                  ]}
                  data={PayableData}
                  identifier={'id'}
                  isViewEnable={false}
                  isEditEnable={false}
                  onBtn={(id) => handleStatus(id)}
                  onEdit={(id) => console.log(id)}
                  onRowClick={(id) =>
                    navigateWithReturn(navigate, `/builder/accounts/payables/${id}`, {
                      returnTo: buildReturnToFromLocation(location),
                      state: { from: 'performaInvoice' }
                    })
                  }
                  isDeleteEnable={false}
                  onDelete={() => {}}
                  isWrap={true}
                />
                <PaginationComponent
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  isFetching={isFetchingPayables}
                />
              </div>
            </GridWrapper>
          </>
        ) : (
          <div className="pt-[3rem]">
            <NoDataFound disabledBtn screenHeight={true} />
          </div>
        )}
      </div>
    </>
  );
};

export default PerformaInvoiceList;
